import { buildInitialValues } from '@/form-utils/build-form-spec';
import type { FieldSpec, FormSpec } from '@/form-utils/form-spec';
import { getPathValue, setPathValue } from '@/form-utils/path-utils';

const INCIDENT_FORM_DRAFT_STORAGE_KEY_PREFIX = 'incident-form-draft:';

type RouteLike = {
    name?: unknown;
    params?: Record<string, unknown>;
};

type IncidentFormDraftRecord = {
    fingerprint: string;
    savedAt: string;
    values: Record<string, unknown>;
};

type DraftRestoreResult =
    | {
          status: 'none';
      }
    | {
          status: 'restored' | 'partial';
          values: Record<string, unknown>;
      };

function getFirstParamValue(value: unknown): string {
    if (Array.isArray(value)) {
        return typeof value[0] === 'string' ? value[0] : '';
    }

    return typeof value === 'string' ? value : '';
}

function cloneDraftValue(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value.map((entry) => cloneDraftValue(entry));
    }

    if (value instanceof Date) {
        return value.toISOString();
    }

    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>).map(([key, entry]) => [key, cloneDraftValue(entry)]),
        );
    }

    return value;
}

function getDraftStorage(): Storage | null {
    if (typeof window !== 'undefined' && window.localStorage) {
        return window.localStorage;
    }

    if (typeof localStorage !== 'undefined') {
        return localStorage;
    }

    return null;
}

function getActiveOrganizationDraftScope(): string {
    if (typeof localStorage === 'undefined') {
        return 'org:none';
    }

    const organizationId = localStorage.getItem('organization_id');
    return `org:${organizationId ?? 'none'}`;
}

function getFieldOptions(field: FieldSpec): Set<string> | null {
    if (!field.options?.length) {
        return null;
    }

    return new Set(field.options.map((option) => option.value));
}

function buildDraftFingerprint(spec: FormSpec): string {
    return JSON.stringify(
        spec.fields.map((field) => ({
            key: field.key,
            kind: field.kind,
            required: field.required,
            nullable: field.nullable ?? false,
            options: field.options?.map((option) => option.value) ?? [],
        })),
    );
}

function serializeIncidentDraftValues(values: Record<string, unknown>, spec: FormSpec): Record<string, unknown> {
    const serialized: Record<string, unknown> = {};

    for (const field of spec.fields) {
        if (field.kind === 'file') {
            continue;
        }

        serialized[field.key] = cloneDraftValue(getPathValue(values, field.path));
    }

    return serialized;
}

function hasRestorableDraftValues(values: Record<string, unknown>, spec: FormSpec): boolean {
    const serializedInitialValues = serializeIncidentDraftValues(buildInitialValues(spec), spec);

    return JSON.stringify(values) !== JSON.stringify(serializedInitialValues);
}

function hasOwnKey(root: Record<string, unknown>, key: string): boolean {
    return Object.prototype.hasOwnProperty.call(root, key);
}

function getSavedDraftValue(savedValues: Record<string, unknown>, field: FieldSpec): unknown {
    if (hasOwnKey(savedValues, field.key)) {
        return savedValues[field.key];
    }

    return getPathValue(savedValues, field.path);
}

function collectSavedDraftKeys(root: Record<string, unknown>, prefix = ''): string[] {
    const keys: string[] = [];

    for (const [key, value] of Object.entries(root)) {
        const resolvedKey = prefix ? `${prefix}.${key}` : key;

        if (Array.isArray(value) || value === null || typeof value !== 'object') {
            keys.push(resolvedKey);
            continue;
        }

        if (key.includes('.')) {
            keys.push(key);
            continue;
        }

        keys.push(...collectSavedDraftKeys(value as Record<string, unknown>, resolvedKey));
    }

    return keys;
}

function sanitizeDraftValue(
    value: unknown,
    field: FieldSpec,
): {
    accepted: boolean;
    value?: unknown;
    partial?: boolean;
} {
    if (field.kind === 'file') {
        return { accepted: false, partial: true };
    }

    if (value === null) {
        return field.nullable ? { accepted: true, value: null } : { accepted: false, partial: true };
    }

    if (field.kind === 'checkbox') {
        return typeof value === 'boolean' ? { accepted: true, value } : { accepted: false, partial: true };
    }

    if (field.kind === 'number') {
        if (typeof value === 'number' && Number.isFinite(value)) {
            return { accepted: true, value };
        }

        if (typeof value === 'string' && value.trim() !== '') {
            const numericValue = Number(value);
            if (Number.isFinite(numericValue)) {
                return { accepted: true, value: numericValue };
            }
        }

        return { accepted: false, partial: true };
    }

    if (field.kind === 'multiselect') {
        if (!Array.isArray(value)) {
            return { accepted: false, partial: true };
        }

        const nextValues = value.filter((entry): entry is string => typeof entry === 'string');
        const allowedOptions = getFieldOptions(field);
        const filteredValues = allowedOptions ? nextValues.filter((entry) => allowedOptions.has(entry)) : nextValues;

        return {
            accepted: true,
            value: filteredValues,
            partial: filteredValues.length !== nextValues.length || nextValues.length !== value.length,
        };
    }

    if (typeof value !== 'string') {
        return { accepted: false, partial: true };
    }

    const allowedOptions = getFieldOptions(field);
    if (allowedOptions && !allowedOptions.has(value) && value !== '') {
        return { accepted: false, partial: true };
    }

    return { accepted: true, value };
}

function readIncidentFormDraftRecord(storageKey: string): IncidentFormDraftRecord | null {
    const storage = getDraftStorage();
    if (!storage) {
        return null;
    }

    try {
        const serializedDraft = storage.getItem(storageKey);
        if (!serializedDraft) {
            return null;
        }

        const parsedDraft = JSON.parse(serializedDraft) as IncidentFormDraftRecord;
        if (
            !parsedDraft ||
            typeof parsedDraft !== 'object' ||
            !parsedDraft.values ||
            typeof parsedDraft.values !== 'object'
        ) {
            return null;
        }

        return parsedDraft;
    } catch {
        return null;
    }
}

export function getIncidentFormDraftStorageKey(route: RouteLike): string {
    const routeName = typeof route.name === 'string' ? route.name : 'incident-create-form';
    const incidentCategoryId = getFirstParamValue(route.params?.incidentCategoryId);
    const incidentTypeId = getFirstParamValue(route.params?.incidentTypeId);

    return `${INCIDENT_FORM_DRAFT_STORAGE_KEY_PREFIX}${getActiveOrganizationDraftScope()}:${routeName}:${incidentCategoryId}:${incidentTypeId}`;
}

export function saveIncidentFormDraft(route: RouteLike, values: Record<string, unknown>, spec: FormSpec) {
    const storage = getDraftStorage();
    if (!storage) {
        return;
    }

    try {
        const storageKey = getIncidentFormDraftStorageKey(route);
        const serializedValues = serializeIncidentDraftValues(values, spec);
        if (!hasRestorableDraftValues(serializedValues, spec)) {
            storage.removeItem(storageKey);
            return;
        }

        storage.setItem(
            storageKey,
            JSON.stringify({
                fingerprint: buildDraftFingerprint(spec),
                savedAt: new Date().toISOString(),
                values: serializedValues,
            } satisfies IncidentFormDraftRecord),
        );
    } catch {
        // Ignore storage errors so draft support never blocks form usage.
    }
}

export function restoreIncidentFormDraft(route: RouteLike, spec: FormSpec): DraftRestoreResult {
    const record = readIncidentFormDraftRecord(getIncidentFormDraftStorageKey(route));
    if (!record) {
        return { status: 'none' };
    }

    const restoredValues = buildInitialValues(spec);
    const fieldByKey = new Map(spec.fields.map((field) => [field.key, field]));
    let isPartial = false;

    for (const savedKey of collectSavedDraftKeys(record.values)) {
        if (!fieldByKey.has(savedKey)) {
            isPartial = true;
        }
    }

    for (const field of spec.fields) {
        if (field.kind === 'file') {
            continue;
        }

        const savedValue = getSavedDraftValue(record.values, field);
        if (savedValue === undefined) {
            continue;
        }

        const sanitized = sanitizeDraftValue(savedValue, field);
        if (!sanitized.accepted) {
            isPartial = true;
            continue;
        }

        if (sanitized.partial) {
            isPartial = true;
        }

        setPathValue(restoredValues, field.path, cloneDraftValue(sanitized.value));
    }

    return {
        status: isPartial ? 'partial' : 'restored',
        values: restoredValues,
    };
}

export function clearIncidentFormDraft(route: RouteLike) {
    const storage = getDraftStorage();
    if (!storage) {
        return;
    }

    try {
        storage.removeItem(getIncidentFormDraftStorageKey(route));
    } catch {
        // Ignore storage errors so draft support never blocks form usage.
    }
}

export function clearAllIncidentFormDrafts() {
    const storage = getDraftStorage();
    if (!storage) {
        return;
    }

    try {
        const keys = Array.from({ length: storage.length }, (_, index) => storage.key(index)).filter(
            (key): key is string => typeof key === 'string',
        );

        for (const key of keys) {
            if (key.startsWith(INCIDENT_FORM_DRAFT_STORAGE_KEY_PREFIX)) {
                storage.removeItem(key);
            }
        }
    } catch {
        // Ignore storage errors so draft support never blocks form usage.
    }
}
